import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Database,
  Download,
  FileDown,
  Gift,
  History,
  Image as ImageIcon,
  Loader2,
  Merge,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Store,
  Trash2,
  Undo2,
  Upload,
} from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/components/Confirm";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ImportDialog from "@/components/ImportDialog";
import FooterSettings from "@/components/FooterSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MODULES = [
  {
    key: "gift_boxes",
    name: "Gift boxes",
    hint: "Build a bundle from several items at once, taking each out of stock together.",
  },
  {
    key: "reorder",
    name: "Reorder",
    hint: "A shopping list of everything out of stock or running low.",
  },
  {
    key: "valuation",
    name: "Valuation",
    hint: "What the stock cost against what it could sell for, by category.",
  },
];

function Section({ title, hint, children, hidden }) {
  if (hidden) return null;
  return (
    <div className="panel panel-pad">
      <div className="eyebrow">{title}</div>
      {hint && <p className="text-[12.5px] text-muted2 mt-1.5 max-w-lg">{hint}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

const SNAPSHOT_LOOK = {
  replace: { icon: Upload, tone: "bg-rose-50 text-stock-out" },
  client_delete: { icon: Trash2, tone: "bg-rose-50 text-stock-out" },
  item_merge: { icon: Merge, tone: "bg-gold-light text-gold" },
  category_merge: { icon: Merge, tone: "bg-gold-light text-gold" },
  manual: { icon: History, tone: "bg-surface text-muted2" },
};

function timeAgo(value) {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return formatDate(value);
}

/** "9 items fewer now" — the difference between a copy and how things stand. */
function describeChanges(changes) {
  if (!changes) return "";
  const words = { items: "item", purchases: "purchase", sales: "sale", giftbox_builds: "gift box" };
  const parts = Object.entries(changes)
    .filter(([, n]) => n)
    .map(([key, n]) => {
      const word = words[key] || key;
      const count = Math.abs(n);
      return `${count} ${word}${count === 1 ? "" : "s"} ${n > 0 ? "fewer" : "more"} now`;
    });
  return parts.slice(0, 2).join(" · ");
}

export default function Settings() {
  const confirm = useConfirm();
  const { settings, refresh } = useSettings();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seeds, setSeeds] = useState([]);
  const [chosen, setChosen] = useState("");
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [snapshots, setSnapshots] = useState([]);

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const loadSnapshots = useCallback(async () => {
    try {
      const { data } = await api.get("/snapshots");
      setSnapshots(data.snapshots || []);
    } catch {
      setSnapshots([]);
    }
  }, []);

  const checkLastImport = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/import/last");
      setLastImport(data.batch);
    } catch {
      setLastImport(null);
    }
  }, []);


  // Starter datasets belong to the owner — one client's dataset must never be
  // loadable by another, so a client admin doesn't even ask for the list.
  useEffect(() => {
    if (!isOwner) {
      setSeeds([]);
      return;
    }
    checkLastImport();
    loadSnapshots();
    api
      .get("/seeds")
      .then(({ data }) => {
        setSeeds(data);
        // Default to the first dataset, without depending on current selection.
        setChosen((current) => current || (data.length ? data[0].key : ""));
      })
      .catch(() => setSeeds([]));
  }, [isOwner, checkLastImport, loadSnapshots]);

  const [shopBusy, setShopBusy] = useState(false);
  const [iconBusy, setIconBusy] = useState(false);
  const iconInput = useRef(null);

  const shopIcon = form.shop?.icon_key ? `/api/photo/${form.shop.icon_key}` : "";

  const uploadShopIcon = async (file) => {
    setIconBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/shop/icon", body);
      // Reflect it at once rather than waiting for the next load.
      setForm((f) => ({
        ...f,
        shop: { ...f.shop, icon_key: data.icon_url.replace("/api/photo/", "") },
      }));
      toast.success("Icon saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setIconBusy(false);
    }
  };

  const removeShopIcon = async () => {
    try {
      await api.delete("/shop/icon");
      setForm((f) => ({ ...f, shop: { ...f.shop, icon_key: "" } }));
      toast.success("Icon removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Departments are the storefront's doorways — a category is grouped into
  // one from the Categories page, but the doorway itself (its name, its
  // optional cover photo) lives here.
  const [departments, setDepartments] = useState([]);
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [deptEditing, setDeptEditing] = useState(null); // null while creating
  const [deptName, setDeptName] = useState("");
  const [deptBusy, setDeptBusy] = useState(false);
  const [deptPhotoBusyId, setDeptPhotoBusyId] = useState(null);
  const deptPhotoInput = useRef(null);
  const deptPhotoTarget = useRef(null);

  const loadDepartments = useCallback(async () => {
    try {
      const { data } = await api.get("/departments");
      setDepartments(data);
    } catch {
      setDepartments([]);
    }
  }, []);

  useEffect(() => {
    if (form.shop?.enabled) loadDepartments();
  }, [form.shop?.enabled, loadDepartments]);

  const openDeptDialog = (dept) => {
    setDeptEditing(dept || null);
    setDeptName(dept?.name || "");
    setDeptDialogOpen(true);
  };

  const saveDept = async () => {
    if (!deptName.trim()) return;
    setDeptBusy(true);
    try {
      if (deptEditing) {
        await api.put(`/departments/${deptEditing.department_id}`, { name: deptName });
      } else {
        await api.post("/departments", { name: deptName });
      }
      toast.success(deptEditing ? "Department renamed" : "Department added");
      setDeptDialogOpen(false);
      loadDepartments();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDeptBusy(false);
    }
  };

  const deleteDept = async (dept) => {
    const ok = await confirm({
      title: `Delete ${dept.name}?`,
      body:
        dept.category_count > 0
          ? `${dept.category_count} categor${dept.category_count === 1 ? "y" : "ies"} in it go back to unassigned — nothing is deleted, they just stop showing on the storefront until you put them in another department.`
          : "This doorway will no longer show on the storefront.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/departments/${dept.department_id}`);
      toast.success("Department deleted");
      loadDepartments();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const uploadDeptPhoto = async (departmentId, file) => {
    setDeptPhotoBusyId(departmentId);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/departments/${departmentId}/photo`, body);
      toast.success("Photo added");
      loadDepartments();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDeptPhotoBusyId(null);
    }
  };

  const removeDeptPhoto = async (departmentId) => {
    try {
      await api.delete(`/departments/${departmentId}/photo`);
      toast.success("Photo removed");
      loadDepartments();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // The built-in "gift boxes" doorway — same photo-or-fallback treatment as
  // a department, but it isn't one, since it groups ready-made boxes rather
  // than categories.
  const [giftboxPhotoBusy, setGiftboxPhotoBusy] = useState(false);
  const giftboxPhotoInput = useRef(null);
  const giftboxPhotoUrl = form.shop?.giftbox_photo_key
    ? `/api/photo/${form.shop.giftbox_photo_key}`
    : "";

  const uploadGiftboxPhoto = async (file) => {
    setGiftboxPhotoBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/shop/giftbox-photo", body);
      setForm((f) => ({
        ...f,
        shop: { ...f.shop, giftbox_photo_key: data.photo_url.replace("/api/photo/", "") },
      }));
      toast.success("Photo saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setGiftboxPhotoBusy(false);
    }
  };

  const removeGiftboxPhoto = async () => {
    try {
      await api.delete("/shop/giftbox-photo");
      setForm((f) => ({ ...f, shop: { ...f.shop, giftbox_photo_key: "" } }));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const setAllInShop = async (value) => {
    const ok = await confirm({
      title: value ? "Add every item to the shop?" : "Take everything out of the shop?",
      body: value
        ? "Everything in Inventory becomes visible to customers. You can remove individual items afterwards."
        : "The shop will show nothing until items are added back.",
      confirmText: value ? "Add everything" : "Take everything out",
      destructive: !value,
    });
    if (!ok) return;
    setShopBusy(true);
    try {
      const { data } = await api.post("/items/shop-all", { in_shop: value });
      toast.success(
        value ? `${data.total} items are now in the shop` : "The shop is empty again"
      );
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setShopBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        business_name: form.business_name,
        code_prefix: form.code_prefix,
        currency_symbol: form.currency_symbol,
        currency_code: form.currency_code,
        markup_multiplier: Number(form.markup_multiplier),
        low_stock_threshold: Number(form.low_stock_threshold),
        gift_box_types: (form.gift_box_types || []).filter(Boolean),
        modules: form.modules,
        labels: form.labels,
        shop: form.shop,
        business_address: form.business_address,
        tax_id: form.tax_id,
        bank_account_number: form.bank_account_number,
        bank_routing_number: form.bank_routing_number,
      });
      toast.success("Settings saved");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const runSeed = async (replace) => {
    const seed = seeds.find((s) => s.key === chosen);
    if (!seed) return;
    const ok = await confirm(
      replace
        ? {
            title: `Replace everything with ${seed.client_name}?`,
            body: "All items, purchases, sales and gift boxes currently here will be deleted first.",
            confirmText: "Replace everything",
            destructive: true,
          }
        : {
            title: `Load the ${seed.client_name} dataset?`,
            body: "Existing item codes are left alone, and anything missing gets added.",
            confirmText: "Load it",
          }
    );
    if (!ok) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed", null, {
        params: { key: chosen, replace },
      });
      const i = data.imported;
      toast.success(`Imported ${i.items} items, ${i.purchases} purchases, ${i.sales} sales`);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSeeding(false);
    }
  };

  // The same columns the importer reads, as a real Excel file so it opens
  // cleanly rather than as a wall of commas.
  const undoImport = async () => {
    const ok = await confirm({
      title: `Remove the ${lastImport.items} items that import added?`,
      body: "Anything already used in a purchase or sale is kept, so no history breaks.",
      confirmText: "Undo the import",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.delete("/admin/import/last");
      toast.success(
        data.kept
          ? `Removed ${data.removed}, kept ${data.kept} already in use`
          : `Removed ${data.removed} items`
      );
      setLastImport(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const takeSnapshot = async () => {
    try {
      await api.post("/snapshots");
      toast.success("Copy saved");
      loadSnapshots();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const restoreSnapshot = async (snap) => {
    const ok = await confirm({
      title: `Go back to ${formatDate(snap.at)}?`,
      body: "Everything here is replaced with how it was at that moment. A copy of right now is saved first, so this is reversible too.",
      confirmText: "Restore",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/snapshots/${snap.snapshot_id}/restore`);
      toast.success(`Restored ${data.restored.items} items`);
      loadSnapshots();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const downloadWorkbook = async () => {
    try {
      const res = await api.get("/export/workbook.csv", { responseType: "text" });
      const XLSX = await import("xlsx");
      const wb = XLSX.read(res.data, { type: "string" });
      XLSX.writeFile(wb, `items-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Downloaded — edit it and upload it back when you're done");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const downloadBackup = async () => {
    try {
      const res = await api.get("/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resetAll = async () => {
    const ok = await confirm({
      title: "Delete everything?",
      body: "Every item, purchase, sale and gift box goes. Accounts and categories stay. This can't be undone.",
      confirmText: "Delete everything",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete("/admin/reset");
      toast.success("All inventory data cleared");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Settings"
      subtitle="These apply to the client you're working in"
      action={
        <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-hover">
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Save changes</span>
            </>
          )}
        </Button>
      }
    >
      <Tabs defaultValue="business" className="max-w-[1400px]">
        <TabsList className="bg-brand-light/60 mb-5">
          <TabsTrigger value="business">Business</TabsTrigger>
          <TabsTrigger value="shop">Shop</TabsTrigger>
          <TabsTrigger value="footer">Footer</TabsTrigger>
          <TabsTrigger value="data">Data</TabsTrigger>
        </TabsList>

        <TabsContent value="business">
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
        <Section title="Business" hint="Shown in the sidebar and on the sign-in screen.">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="bn">Business name</Label>
              <Input
                id="bn"
                value={form.business_name || ""}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cs">Currency symbol</Label>
                <Input
                  id="cs"
                  value={form.currency_symbol || ""}
                  onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cc">Currency code</Label>
                <Input
                  id="cc"
                  value={form.currency_code || ""}
                  onChange={(e) => setForm({ ...form, currency_code: e.target.value })}
                  placeholder="USD"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp">Item code prefix</Label>
              <Input
                id="cp"
                value={form.code_prefix || ""}
                onChange={(e) => setForm({ ...form, code_prefix: e.target.value.toUpperCase() })}
                maxLength={4}
                className="font-mono w-28"
              />
              <p className="text-[12px] text-muted2">
                The first part of every code, as in{" "}
                <span className="font-mono">{form.code_prefix || "JJ"}-BF-001</span>.
              </p>
            </div>
          </div>
        </Section>

        <Section
          title="Invoice defaults"
          hint="What a new invoice starts from — override any of it for just one invoice from the Invoices page."
        >
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="iaddr">Business address</Label>
              <Textarea
                id="iaddr"
                rows={2}
                value={form.business_address || ""}
                onChange={(e) => setForm({ ...form, business_address: e.target.value })}
                placeholder="Shown under your business name on every invoice"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="itax">Tax ID</Label>
              <Input
                id="itax"
                value={form.tax_id || ""}
                onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ibank">Bank account number</Label>
                <Input
                  id="ibank"
                  type="password"
                  value={form.bank_account_number || ""}
                  onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })}
                  placeholder="•••• •••• ••••"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="iroute">Routing number</Label>
                <Input
                  id="iroute"
                  value={form.bank_routing_number || ""}
                  onChange={(e) => setForm({ ...form, bank_routing_number: e.target.value })}
                />
              </div>
            </div>
            <p className="text-[12px] text-muted2">
              Only the last four digits ever show on an invoice — the rest stays here.
            </p>
          </div>
        </Section>

        <Section
          title="Stock rules"
          hint="These two numbers drive the reorder list and the suggested selling price."
        >
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="lt">Reorder threshold</Label>
              <Input
                id="lt"
                type="number"
                min="0"
                value={form.low_stock_threshold ?? 3}
                onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })}
                className="w-28"
              />
              <p className="text-[12px] text-muted2">
                An item is flagged once it drops to this many units or fewer.
              </p>
            </div>
            <Separator />
            <div className="space-y-1.5">
              <Label htmlFor="mm">Markup multiplier</Label>
              <Input
                id="mm"
                type="number"
                step="0.1"
                min="0.1"
                value={form.markup_multiplier ?? 2.5}
                onChange={(e) => setForm({ ...form, markup_multiplier: e.target.value })}
                className="w-28"
              />
              <p className="text-[12px] text-muted2">
                Suggests a price when you add an item without one. At{" "}
                {form.markup_multiplier || 2.5}×, a {form.currency_symbol}400 item is
                suggested at {form.currency_symbol}
                {Math.round(400 * (Number(form.markup_multiplier) || 2.5))}.
              </p>
            </div>
          </div>
        </Section>

        <Section
          title="Features"
          hint="Switch off anything this business doesn't do, and use their own word for the rest. Inventory, Purchases and Sales can't be switched off — the stock figure is made of them."
        >
          <div className="space-y-3">
            {MODULES.map((m) => {
              const on = form.modules?.[m.key] !== false;
              return (
                <div
                  key={m.key}
                  className="rounded-xl border border-line p-3.5 flex items-start gap-3"
                >
                  <Switch
                    checked={on}
                    onCheckedChange={(v) =>
                      setForm({ ...form, modules: { ...(form.modules || {}), [m.key]: v } })
                    }
                    className="mt-0.5 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-medium text-ink">{m.name}</div>
                    <p className="text-[12px] text-muted2 mt-0.5">{m.hint}</p>
                    {on && (
                      <div className="mt-2.5">
                        <Label className="text-[12px] text-muted2">Call it</Label>
                        <Input
                          value={form.labels?.[m.key] ?? m.name}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              labels: { ...(form.labels || {}), [m.key]: e.target.value },
                            })
                          }
                          placeholder={m.name}
                          className="mt-1 h-9"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section
          title={`${form.labels?.gift_boxes || "Gift box"} types`}
          hint="Suggestions offered when building one. One per line."
          hidden={form.modules?.gift_boxes === false}
        >
          <textarea
            value={(form.gift_box_types || []).join("\n")}
            onChange={(e) =>
              setForm({ ...form, gift_box_types: e.target.value.split("\n") })
            }
            rows={6}
            className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
          />
        </Section>

        {/* Framed as ownership and portability rather than insurance. A client
            shouldn't be left wondering whether their data is at risk — that's
            the operator's concern, and the reminder for it is owner-only. */}
          </div>
        </TabsContent>

        <TabsContent value="shop">
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
        <Section
          title="Shop window"
          hint="A public page showing what you have, reading straight from your inventory. Customers never see what anything cost you, or how many you hold."
        >
          <div className="space-y-4">
            <div className="rounded-xl border border-line p-3.5 flex items-start gap-3">
              <Switch
                checked={!!form.shop?.enabled}
                onCheckedChange={(v) =>
                  setForm({ ...form, shop: { ...(form.shop || {}), enabled: v } })
                }
                className="mt-0.5 shrink-0"
              />
              <div>
                <div className="text-[13.5px] font-medium text-ink">Shop is live</div>
                <p className="text-[12px] text-muted2 mt-0.5">
                  Off until you're ready. Items are hidden until you add them from
                  Inventory.
                </p>
              </div>
            </div>
          </div>
        </Section>

        <Section
          title="What's in the shop"
          hint="Items are hidden until added. Add them all to begin with, then take out anything that shouldn't be public."
        >
          {form.shop?.enabled ? (
            <div className="space-y-2.5">
              <Button
                variant="outline"
                className="w-full"
                disabled={shopBusy}
                onClick={() => setAllInShop(true)}
              >
                <Store className="w-4 h-4 mr-2" /> Add everything
              </Button>
              <Button
                variant="outline"
                className="w-full text-muted2"
                disabled={shopBusy}
                onClick={() => setAllInShop(false)}
              >
                Take everything out
              </Button>
              <p className="text-[12px] text-muted2 leading-relaxed">
                Individual items are added or removed from Inventory, by opening
                one and using the shop switch.
              </p>
            </div>
          ) : (
            <p className="text-[12.5px] text-muted2">
              Switch the shop on first.
            </p>
          )}
        </Section>

        <Section
          title="Departments"
          hint="A doorway on the shop — walk into one and see what's grouped inside. A cover photo is optional; skip it and we pull one from an item inside instead."
        >
          {form.shop?.enabled ? (
            <div className="space-y-4">
              <input
                ref={deptPhotoInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file || !deptPhotoTarget.current) return;
                  await uploadDeptPhoto(deptPhotoTarget.current, file);
                  deptPhotoTarget.current = null;
                }}
              />

              {departments.length === 0 ? (
                <p className="text-[12.5px] text-muted2">
                  None yet. Add one, then assign categories to it from the
                  Categories page.
                </p>
              ) : (
                <div className="space-y-0.5">
                  {departments.map((d) => (
                    <div
                      key={d.department_id}
                      className="flex items-center gap-2.5 py-2 border-b border-line last:border-0"
                    >
                      <div className="w-9 h-9 rounded-lg border border-line bg-surface overflow-hidden shrink-0 flex items-center justify-center">
                        {d.photo_url ? (
                          <img src={d.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-3.5 h-3.5 text-muted2" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium text-ink truncate">{d.name}</div>
                        <div className="text-[11px] text-muted2">
                          {d.category_count} categor{d.category_count === 1 ? "y" : "ies"} ·{" "}
                          {d.item_count} item{d.item_count === 1 ? "" : "s"}
                          {!d.photo_url && " · auto photo"}
                        </div>
                        {/* Being grouped into a department only decides where an item
                            would show up — it still has to be switched into the shop
                            itself first. This is the single most common "why isn't my
                            department showing" cause, so it's called out right here
                            instead of leaving it to be discovered on the storefront. */}
                        {d.category_count > 0 && d.shop_item_count === 0 && (
                          <div className="flex items-start gap-1.5 text-[11px] text-gold mt-1 leading-snug">
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-[1.5px]" />
                            <span>
                              Won't show on the storefront yet — none of its items are
                              switched into the shop. Open an item and turn on{" "}
                              <span className="font-medium">Add to your shop</span>.
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-0.5 shrink-0">
                        <button
                          onClick={() => {
                            deptPhotoTarget.current = d.department_id;
                            deptPhotoInput.current?.click();
                          }}
                          disabled={deptPhotoBusyId === d.department_id}
                          className="p-1.5 rounded-lg text-muted2 hover:text-brand hover:bg-brand-light"
                          title={d.photo_url ? "Change photo" : "Add a photo"}
                        >
                          <ImageIcon className="w-3.5 h-3.5" />
                        </button>
                        {d.photo_url && (
                          <button
                            onClick={() => removeDeptPhoto(d.department_id)}
                            className="p-1.5 rounded-lg text-muted2 hover:text-stock-out hover:bg-rose-50"
                            title="Remove photo"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => openDeptDialog(d)}
                          className="p-1.5 rounded-lg text-muted2 hover:text-brand hover:bg-brand-light"
                          title="Rename"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deleteDept(d)}
                          className="p-1.5 rounded-lg text-muted2 hover:text-stock-out hover:bg-rose-50"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Button variant="outline" className="w-full" onClick={() => openDeptDialog(null)}>
                <Plus className="w-4 h-4 mr-2" /> Add department
              </Button>

              <p className="text-[12px] text-muted2 leading-relaxed">
                Assign a category to a department from{" "}
                <Link to="/categories" className="link-quiet">
                  the Categories page
                </Link>
                .
              </p>

              {form.modules?.gift_boxes && (
                <div className="pt-1 border-t border-line space-y-2">
                  <div className="flex items-center gap-3 pt-3.5">
                    <div className="w-9 h-9 rounded-lg border border-line bg-surface overflow-hidden shrink-0 flex items-center justify-center">
                      {giftboxPhotoUrl ? (
                        <img src={giftboxPhotoUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Gift className="w-3.5 h-3.5 text-muted2" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-ink">
                        {(form.labels?.gift_boxes || "Gift boxes")} doorway
                      </div>
                      <div className="text-[11px] text-muted2">
                        {giftboxPhotoUrl
                          ? "Cover photo set"
                          : "No photo — falls back to a ready-made box's photo"}
                      </div>
                    </div>
                    <input
                      ref={giftboxPhotoInput}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (file) uploadGiftboxPhoto(file);
                      }}
                    />
                    <div className="flex gap-0.5 shrink-0">
                      <button
                        onClick={() => giftboxPhotoInput.current?.click()}
                        disabled={giftboxPhotoBusy}
                        className="p-1.5 rounded-lg text-muted2 hover:text-brand hover:bg-brand-light"
                        title={giftboxPhotoUrl ? "Change photo" : "Add a photo"}
                      >
                        <ImageIcon className="w-3.5 h-3.5" />
                      </button>
                      {giftboxPhotoUrl && (
                        <button
                          onClick={removeGiftboxPhoto}
                          className="p-1.5 rounded-lg text-muted2 hover:text-stock-out hover:bg-rose-50"
                          title="Remove photo"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[12px] text-muted2 leading-relaxed">
                    Shows automatically once a box is ready-made or "build your
                    own" is switched on, below under Ordering.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-[12.5px] text-muted2">
              Switch the shop on first.
            </p>
          )}
        </Section>

        <Section
          title="Words"
          hint="What customers read at the top and bottom of the page."
        >
          {form.shop?.enabled && (
            <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="sh-head">Headline</Label>
                  <Input
                    id="sh-head"
                    value={form.shop?.headline || ""}
                    onChange={(e) =>
                      setForm({ ...form, shop: { ...form.shop, headline: e.target.value } })
                    }
                    placeholder={form.business_name}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sh-tag">Underneath</Label>
                  <Input
                    id="sh-tag"
                    value={form.shop?.tagline || ""}
                    onChange={(e) =>
                      setForm({ ...form, shop: { ...form.shop, tagline: e.target.value } })
                    }
                    placeholder="Made fresh · delivery islandwide"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sh-wa">WhatsApp number</Label>
                  <Input
                    id="sh-wa"
                    value={form.shop?.whatsapp || ""}
                    onChange={(e) =>
                      setForm({ ...form, shop: { ...form.shop, whatsapp: e.target.value } })
                    }
                    placeholder="876 000 0000"
                  />
                  <p className="text-[12px] text-muted2">
                    Include the country code. Customers tap it to order.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>Three short tags</Label>
                  <Input
                    value={(form.shop?.tags || []).join(", ")}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        shop: { ...form.shop, tags: e.target.value.split(",").map((t) => t.trim()) },
                      })
                    }
                    placeholder="Delivery islandwide, Custom boxes, Same-day"
                  />
                  <p className="text-[12px] text-muted2">Separated by commas.</p>
                </div>
            </div>
          )}
        </Section>

        <Section
          title="Look"
          hint="How the page is laid out and coloured."
        >
          {form.shop?.enabled && (
            <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Home screen icon</Label>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl border border-line bg-surface overflow-hidden shrink-0 flex items-center justify-center">
                      {shopIcon ? (
                        <img src={shopIcon} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Store className="w-5 h-5 text-muted2" />
                      )}
                    </div>
                    <input
                      ref={iconInput}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (file) uploadShopIcon(file);
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={iconBusy}
                      onClick={() => iconInput.current?.click()}
                    >
                      {iconBusy ? "Uploading…" : shopIcon ? "Change" : "Choose a file"}
                    </Button>
                    {shopIcon && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted2 hover:text-stock-out"
                        onClick={removeShopIcon}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-[12px] text-muted2 leading-relaxed">
                    Shown when someone adds the shop to their phone. A square PNG
                    works best. Without one it's a plain tile in the shop colour.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>Shop colour</Label>
                  <div className="flex gap-2.5">
                    {[
                      ["berry", "#8E2A5B"],
                      ["forest", "#1D6F5C"],
                      ["navy", "#2C4A7C"],
                      ["clay", "#A5471F"],
                      ["plum", "#4A3B63"],
                      ["ink", "#1F1F23"],
                    ].map(([name, hex]) => (
                      <button
                        key={name}
                        aria-label={name}
                        onClick={() => setForm({ ...form, shop: { ...form.shop, colour: name } })}
                        className={cn(
                          "w-8 h-8 rounded-[10px] border-2 transition-colors",
                          (form.shop?.colour || "berry") === name
                            ? "border-ink"
                            : "border-transparent"
                        )}
                        style={{ background: hex }}
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Layout</Label>
                  <div className="flex gap-2">
                    {[
                      { key: "grid", label: "Grid" },
                      { key: "list", label: "List" },
                    ].map((o) => (
                      <button
                        key={o.key}
                        onClick={() => setForm({ ...form, shop: { ...form.shop, layout: o.key } })}
                        className={cn(
                          "flex-1 rounded-xl border py-2 text-[12.5px] transition-colors",
                          (form.shop?.layout || "grid") === o.key
                            ? "border-brand bg-brand-light text-brand"
                            : "border-line text-muted2"
                        )}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

            </div>
          )}
        </Section>

        <Section
          title="What customers see"
          hint="Anything not ticked stays private."
        >
          {form.shop?.enabled && (
            <div className="space-y-4">
                <div className="space-y-2 pt-1">
                  <Label>What customers see</Label>
                  {[
                    { key: "show_prices", label: "Prices" },
                    { key: "show_availability", label: "Whether something's sold out" },
                    { key: "show_search", label: "A search box" },
                    { key: "hide_sold_out", label: "Hide sold-out items entirely" },
                  ].map((o) => (
                    <label key={o.key} className="flex items-center gap-3 cursor-pointer">
                      <Switch
                        checked={!!form.shop?.[o.key]}
                        onCheckedChange={(v) =>
                          setForm({ ...form, shop: { ...form.shop, [o.key]: v } })
                        }
                      />
                      <span className="text-[13px] text-ink">{o.label}</span>
                    </label>
                  ))}
                  <p className="text-[12px] text-muted2 pt-1 leading-relaxed">
                    Never shown, whatever these say: what things cost you, exact
                    stock counts, suppliers and item codes.
                  </p>
                </div>
            </div>
          )}
        </Section>

        <Section
          title="Ordering"
          hint="How customers put an order together."
        >
          {form.shop?.enabled && (
            <div className="space-y-4">
                <div className="space-y-2 pt-1">
                  <Label>Ordering</Label>
                  {[
                    { key: "allow_cart", label: "Let customers add to a basket" },
                    { key: "allow_box", label: "Let customers build their own box" },
                  ].map((o) => (
                    <label key={o.key} className="flex items-center gap-3 cursor-pointer">
                      <Switch
                        checked={!!form.shop?.[o.key]}
                        onCheckedChange={(v) =>
                          setForm({ ...form, shop: { ...form.shop, [o.key]: v } })
                        }
                      />
                      <span className="text-[13px] text-ink">{o.label}</span>
                    </label>
                  ))}
                  <p className="text-[12px] text-muted2 pt-1 leading-relaxed">
                    Nothing is paid or held here — a basket just means the order
                    arrives as one message instead of several questions.
                  </p>
                </div>

                {form.shop?.allow_box && (
                  <div className="rounded-xl border border-line p-3.5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="bs">Fewest items</Label>
                        <Input
                          id="bs"
                          type="number"
                          min="2"
                          value={form.shop?.box_min ?? 5}
                          onChange={(e) =>
                            setForm({ ...form, shop: { ...form.shop, box_min: e.target.value } })
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="bx">Most items</Label>
                        <Input
                          id="bx"
                          type="number"
                          min="2"
                          value={form.shop?.box_max ?? 5}
                          onChange={(e) =>
                            setForm({ ...form, shop: { ...form.shop, box_max: e.target.value } })
                          }
                        />
                        <p className="text-[12px] text-muted2">
                          Same as the fewest for a fixed size.
                        </p>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="bp">Box price</Label>
                        <Input
                          id="bp"
                          type="number"
                          min="0"
                          value={form.shop?.box_price ?? 0}
                          onChange={(e) =>
                            setForm({ ...form, shop: { ...form.shop, box_price: e.target.value } })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Which categories can go in</Label>
                      <Input
                        value={(form.shop?.box_categories || []).join(", ")}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            shop: {
                              ...form.shop,
                              box_categories: e.target.value.split(",").map((c) => c.trim()),
                            },
                          })
                        }
                        placeholder="Leave blank for anything in the shop"
                      />
                    </div>
                    <p className="text-[12px] text-muted2 leading-relaxed">
                      When the order comes in, build the box in the app as usual —
                      stock comes off then, not when a customer picks.
                    </p>
                  </div>
                )}
            </div>
          )}
        </Section>

        <Section
          title="Address"
          hint="Where the shop lives."
        >
          {form.shop?.enabled && (
            <div className="space-y-4">
                <div className="space-y-1.5 pt-1">
                  <Label htmlFor="sh-dom">Their own address</Label>
                  <Input
                    id="sh-dom"
                    value={form.shop?.domain || ""}
                    onChange={(e) =>
                      setForm({ ...form, shop: { ...form.shop, domain: e.target.value } })
                    }
                    placeholder="shop.theirbusiness.com"
                  />
                  <p className="text-[12px] text-muted2 leading-relaxed">
                    Optional. Leave blank and the shop lives at the address below.
                  </p>
                </div>

                {form.shop?.domain && (
                  <div className="rounded-xl border border-line p-3.5 space-y-2.5">
                    <div className="text-[12.5px] font-medium text-ink">
                      To make {form.shop.domain} work
                    </div>
                    <div className="text-[12px] text-muted2 leading-relaxed">
                      They add one record where their domain is managed:
                    </div>
                    <div className="rounded-lg bg-surface px-3 py-2.5 font-mono text-[11.5px] text-ink space-y-1">
                      <div>Type: CNAME</div>
                      <div>Name: {form.shop.domain.split(".")[0]}</div>
                      <div className="break-all">
                        Value: {settings.cname_target || window.location.host}
                      </div>
                    </div>
                    <p className="text-[12px] text-muted2 leading-relaxed">
                      Then the certificate is added on our side. Until both are done,
                      that address won't load.
                    </p>
                    {!settings.cname_target && (
                      <p className="text-[12px] text-gold leading-relaxed">
                        That value is a guess — set CNAME_TARGET on the server so it
                        points at the app itself rather than a domain.
                      </p>
                    )}
                  </div>
                )}

                <div className="rounded-xl bg-brand-light px-3.5 py-3">
                  <div className="text-[12px] text-muted2">Your shop address</div>
                  <a
                    href={`/shop/${settings.slug || ""}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-[12.5px] text-brand break-all hover:underline"
                  >
                    {window.location.origin}/shop/{settings.slug || ""}
                  </a>
                </div>
            </div>
          )}
        </Section>

          </div>
        </TabsContent>

        <TabsContent value="footer">
          <FooterSettings
            shop={form.shop || {}}
            onChange={(shop) => setForm((f) => ({ ...f, shop }))}
            businessName={form.business_name}
            shopEnabled={!!form.shop?.enabled}
          />
        </TabsContent>

        <TabsContent value="data">
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
        <Section
          title="Go back in time"
          hint="A copy is saved automatically before anything that wipes data. You can also take one yourself before making big changes."
        >
          <Button variant="outline" onClick={takeSnapshot} className="w-full">
            <History className="w-4 h-4 mr-2" /> Save a copy of right now
          </Button>

          {snapshots.length > 0 ? (
            <div className="mt-3 space-y-2">
              {snapshots.map((snap) => {
                const look = SNAPSHOT_LOOK[snap.reason] || SNAPSHOT_LOOK.manual;
                const Icon = look.icon;
                const c = snap.counts || {};
                const diff = describeChanges(snap.changes);
                return (
                  <div
                    key={snap.snapshot_id}
                    className="rounded-xl border border-line px-3.5 py-3 flex items-start gap-3"
                  >
                    <span
                      className={cn(
                        "w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
                        look.tone
                      )}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-ink">
                        {snap.reason_label}
                      </div>
                      <div className="text-[11.5px] text-muted2 mt-0.5">
                        {timeAgo(snap.at)}
                      </div>
                      <div className="text-[11.5px] text-muted2 font-mono mt-1.5">
                        {c.items || 0} items · {c.purchases || 0} purchases ·{" "}
                        {c.sales || 0} sales
                      </div>
                      {/* The line that actually decides which one you want. */}
                      {diff && <div className="text-[11.5px] text-gold mt-1">{diff}</div>}
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restoreSnapshot(snap)}
                      className="shrink-0"
                    >
                      Restore
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[12px] text-muted2 mt-3">
              No copies yet. One is saved automatically the first time something
              destructive happens.
            </p>
          )}
        </Section>

        <Section
          title="Export your data"
          hint="Your items, purchases, sales, gift boxes and settings in one file. It's your data — take a copy whenever you like."
        >
          <Button variant="outline" onClick={downloadBackup} className="w-full">
            <Download className="w-4 h-4 mr-2" /> Download my data
          </Button>
          <p className="text-[12px] text-muted2 mt-3">
            Handy for your own records, or before making a big change like
            replacing everything with an import.
          </p>
        </Section>

        <Section
          title="Work in a spreadsheet"
          hint="Download your items, edit them anywhere — on a laptop with no signal if you like — then upload the same file back."
        >
          <div className="space-y-2.5">
            <Button variant="outline" onClick={downloadWorkbook} className="w-full">
              <FileDown className="w-4 h-4 mr-2" /> Download items for editing
            </Button>
            <Button variant="outline" onClick={() => setImporting(true)} className="w-full">
              <Upload className="w-4 h-4 mr-2" /> Upload a spreadsheet
            </Button>
          </div>
          {lastImport && lastImport.remaining > 0 && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-gold-light p-3.5">
              <div className="text-[13px] font-medium text-ink">
                Last import added {lastImport.items} item
                {lastImport.items === 1 ? "" : "s"}
              </div>
              <p className="text-[12px] text-muted2 mt-1">
                {formatDate(lastImport.at)}. If that wasn't what you wanted, you can
                take them back out.
              </p>
              <Button
                variant="outline"
                onClick={undoImport}
                className="mt-2.5 border-amber-300 text-gold hover:bg-amber-100 hover:text-gold"
              >
                <Undo2 className="w-4 h-4 mr-2" /> Undo that import
              </Button>
            </div>
          )}
          <p className="text-[12px] text-muted2 mt-3 leading-relaxed">
            Keep the Item Code column as it is and choose{" "}
            <span className="text-ink">Update existing</span> when uploading, and your
            edits land on the right items. Any CSV or Excel file works — only the item
            name is required.
          </p>
        </Section>

        <Section
          hidden={!isOwner}
          title="Starter data"
          hint="Datasets converted from a client's existing spreadsheet. Loading one also applies that client's name, currency and code prefix."
        >
          <div className="space-y-3">
            {seeds.length === 0 ? (
              <p className="text-[13px] text-muted2">
                No datasets on this deployment. Add a JSON file to{" "}
                <span className="font-mono text-[12px]">backend/seeds/</span> to make one
                available here.
              </p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label>Dataset</Label>
                  <Select value={chosen} onValueChange={setChosen}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {seeds.map((s) => (
                        <SelectItem key={s.key} value={s.key}>
                          {s.client_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {seeds.find((s) => s.key === chosen) && (
                    <p className="text-[12px] text-muted2">
                      {(() => {
                        const s = seeds.find((x) => x.key === chosen);
                        return `${s.items} items · ${s.purchases} purchases · ${s.sales} sales · ${s.categories} categories`;
                      })()}
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2.5">
                  <Button
                    variant="outline"
                    onClick={() => runSeed(false)}
                    disabled={seeding || !chosen}
                    className="flex-1"
                  >
                    {seeding ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" /> Load, keep existing
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => runSeed(true)}
                    disabled={seeding || !chosen}
                    className="flex-1"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" /> Replace everything
                  </Button>
                </div>
              </>
            )}

            <Separator className="my-4" />

            <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4">
              <div className="text-[13.5px] font-medium text-ink">Clear all inventory data</div>
              <p className="text-[12.5px] text-muted2 mt-1">
                Removes every item and log entry. Accounts and categories are kept.
              </p>
              <Button
                variant="outline"
                onClick={resetAll}
                className="mt-3 border-rose-200 text-stock-out hover:bg-rose-100 hover:text-stock-out"
              >
                Clear everything
              </Button>
            </div>
          </div>
        </Section>

          </div>
        </TabsContent>
      </Tabs>

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        onImported={() => {
          refresh();
          checkLastImport();
    loadSnapshots();
        }}
      />

      <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {deptEditing ? "Rename department" : "Add a department"}
            </DialogTitle>
            <DialogDescription>
              This is what shows as a doorway on the storefront.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            <Label htmlFor="deptname">Name</Label>
            <Input
              id="deptname"
              value={deptName}
              onChange={(e) => setDeptName(e.target.value)}
              placeholder="e.g. Fine Jewelry"
              autoFocus
            />
            {!deptEditing && (
              <p className="text-[12px] text-muted2">
                A cover photo can be added afterwards from the list.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeptDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveDept} disabled={deptBusy || !deptName.trim()} className="bg-brand hover:bg-brand-hover">
              {deptEditing ? "Save" : "Add department"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
